import type { TNMIAddress, TNMIServicesRequired } from './types';

import {
  isAllAddressesMirnNotFound,
  isAllAddressesNMIHardStop,
  isAllAddressesNMIServiceable,
  isExactMatchForNMI,
  isRequiredToConfirmAddress,
  maskNMI,
} from './utils';
import { searchInternalCesAddresses, type Address } from '@origin-digital/address-lookup';

const getNumericAddressIdentifiers = (address: unknown): string[] => {
  if (typeof address !== 'string') return [];

  return (
    address
      .normalize('NFKC')
      .toLocaleUpperCase('en-AU')
      .match(/\d+[A-Z]*/gu) ?? []
  );
};

const hasMatchingNumericAddressIdentifiers = (
  selectedIdentifiers: string[],
  candidateAddress: unknown
): boolean => {
  const candidateIdentifiers = getNumericAddressIdentifiers(candidateAddress);

  return (
    selectedIdentifiers.length === candidateIdentifiers.length &&
    selectedIdentifiers.every((identifier, index) => identifier === candidateIdentifiers[index])
  );
};

const checkIfCesAddress = async (address: Address | TNMIAddress) => {
  const label = 'qasAddress' in address ? address.qasAddress.label : address.label;

  if (!label) return { isCes: false, searchTerm: '' };

  const selectedIdentifiers = getNumericAddressIdentifiers(label);
  if (selectedIdentifiers.length === 0) return { isCes: false, searchTerm: label };

  const properties = await searchInternalCesAddresses(label);
  // searchTerm (the address label) is forwarded to the new CES app CTA so it can
  // pre-fill / re-run the search once it supports the param.
  return {
    isCes: properties.some(({ propertyAddress }) =>
      hasMatchingNumericAddressIdentifiers(selectedIdentifiers, propertyAddress)
    ),
    searchTerm: label,
  };
};

export const useNMIResolution = () => {
  const isExactMatch = (addresses: TNMIAddress[]): boolean => isExactMatchForNMI(addresses);

  const isNMIBelowThreshold = (
    addresses: TNMIAddress[],
    servicesRequired: TNMIServicesRequired
  ): boolean => {
    return (
      servicesRequired.electricity &&
      !isExactMatchForNMI(addresses) &&
      isRequiredToConfirmAddress(addresses)
    );
  };

  const isNMIHardStop = (addresses: TNMIAddress[]): boolean => {
    return isAllAddressesNMIHardStop(addresses);
  };

  const isNMIServiceable = (addresses: TNMIAddress[]): boolean => {
    return isAllAddressesNMIServiceable(addresses);
  };

  const isMirnNotFound = (addresses: TNMIAddress[]): boolean => {
    return isAllAddressesMirnNotFound(addresses);
  };

  const maskNMILabel = (nmi?: string): string => {
    const maskedNmi = nmi ? maskNMI(nmi) : undefined;
    return maskedNmi ? `NMI ${maskedNmi}` : '';
  };

  return {
    maskNMILabel,
    isNMIBelowThreshold,
    isNMIHardStop,
    isNMIServiceable,
    isExactMatch,
    isMirnNotFound,
    checkIfCesAddress,
  };
};
